import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, AlertTriangle, CheckCircle, Info, Trash2, Shield,
  Database, RefreshCw, Loader2, User, Mail, Lock, Eye, EyeOff, KeyRound
} from 'lucide-react';

export default function AdminSettings() {
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  const [testMode, setTestMode] = useState(false);
  const [testDataCount, setTestDataCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Email change state
  const [emailForm, setEmailForm] = useState({
    new_email: '',
    current_password: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [emailErrors, setEmailErrors] = useState({});

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchSettings();
    fetchTestDataCount();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestMode(res.data.test_mode);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestDataCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/test-data/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestDataCount(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleTestMode = async (checked) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings`, { test_mode: checked }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestMode(checked);
      toast.success(checked ? 'Modo teste ATIVADO' : 'Modo teste DESATIVADO');
    } catch (error) {
      toast.error('Erro ao alterar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTestData = async () => {
    if (!window.confirm('Tem certeza que deseja remover TODOS os dados de teste?')) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/admin/test-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dados de teste removidos!');
      fetchTestDataCount();
    } catch (error) {
      toast.error('Erro ao remover dados de teste');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetDatabase = async () => {
    const confirmed = window.prompt(
      'ATENÇÃO: Esta ação é IRREVERSÍVEL!\n\nTodos os dados (exceto admin) serão PERMANENTEMENTE REMOVIDOS.\n\nDigite "CONFIRMAR" para continuar:'
    );
    
    if (confirmed !== 'CONFIRMAR') {
      toast.error('Operação cancelada');
      return;
    }
    
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/reset-database`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banco de dados resetado!');
      fetchTestDataCount();
    } catch (error) {
      toast.error('Erro ao resetar banco de dados');
    } finally {
      setResetting(false);
    }
  };

  // Validação do formulário de email
  const validateEmailForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailForm.new_email) {
      errors.new_email = 'Novo email é obrigatório';
    } else if (!emailRegex.test(emailForm.new_email)) {
      errors.new_email = 'Formato de email inválido';
    } else if (emailForm.new_email.toLowerCase() === user?.email?.toLowerCase()) {
      errors.new_email = 'O novo email é igual ao email atual';
    }
    
    if (!emailForm.current_password) {
      errors.current_password = 'Senha é obrigatória para confirmar alteração';
    }
    
    setEmailErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit alteração de email
  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!validateEmailForm()) return;
    
    setEmailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/change-email`, {
        current_password: emailForm.current_password,
        new_email: emailForm.new_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email alterado com sucesso!');
      
      // Atualizar contexto do usuário com novo email
      if (res.data.new_email && user) {
        const updatedUser = { ...user, email: res.data.new_email };
        login(token, updatedUser);
      }
      
      // Limpar formulário
      setEmailForm({ new_email: '', current_password: '' });
      setEmailErrors({});
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao alterar email';
      toast.error(detail);
      
      if (detail.includes('Senha')) {
        setEmailErrors({ current_password: detail });
      } else if (detail.includes('email')) {
        setEmailErrors({ new_email: detail });
      }
    } finally {
      setEmailLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Configurações" subtitle="Carregando...">
        <div className="space-y-6">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Configurações</h1>
          <p className="text-slate-500">Parâmetros gerais do sistema</p>
        </div>

        {/* Perfil do Administrador */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-[#2563EB]/10 p-3 rounded-lg">
              <User className="w-6 h-6 text-[#2563EB]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Perfil do Administrador</h3>
              <p className="text-sm text-slate-500">Gerencie suas credenciais de acesso</p>
            </div>
          </div>
          
          {/* Email atual */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <Label className="text-xs text-slate-500 uppercase tracking-wide">Email Atual</Label>
            <div className="flex items-center gap-2 mt-1">
              <Mail className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{user?.email}</span>
            </div>
          </div>
          
          <Separator className="my-6" />
          
          {/* Formulário de alteração de email */}
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#2563EB]" />
              Alterar Email do Administrador
            </h4>
            
            <div>
              <Label htmlFor="new_email">Novo Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="new_email"
                  type="email"
                  placeholder="Digite o novo email"
                  className={`pl-10 ${emailErrors.new_email ? 'border-red-500' : ''}`}
                  value={emailForm.new_email}
                  onChange={(e) => {
                    setEmailForm({ ...emailForm, new_email: e.target.value });
                    setEmailErrors({ ...emailErrors, new_email: '' });
                  }}
                  data-testid="new-email-input"
                />
              </div>
              {emailErrors.new_email && (
                <p className="text-red-500 text-xs mt-1">{emailErrors.new_email}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="email_password">Senha Atual (para confirmação)</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email_password"
                  type={showEmailPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha atual"
                  className={`pl-10 pr-10 ${emailErrors.current_password ? 'border-red-500' : ''}`}
                  value={emailForm.current_password}
                  onChange={(e) => {
                    setEmailForm({ ...emailForm, current_password: e.target.value });
                    setEmailErrors({ ...emailErrors, current_password: '' });
                  }}
                  data-testid="email-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowEmailPassword(!showEmailPassword)}
                >
                  {showEmailPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {emailErrors.current_password && (
                <p className="text-red-500 text-xs mt-1">{emailErrors.current_password}</p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                type="submit"
                className="bg-[#2563EB] hover:bg-[#2563EB]/90"
                disabled={emailLoading}
                data-testid="change-email-btn"
              >
                {emailLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Alterando...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Alterar Email
                  </>
                )}
              </Button>
              
              <Button 
                type="button"
                variant="outline"
                onClick={() => navigate('/control-portal-clube/change-password')}
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Alterar Senha
              </Button>
            </div>
          </form>
        </Card>

        {/* Modo Teste */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold">Modo Teste (Sandbox)</h3>
                {testMode ? (
                  <Badge className="bg-yellow-100 text-yellow-700">ATIVO</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-700">PRODUÇÃO</Badge>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Quando ativo, pagamentos não serão cobrados e todos os dados criados serão marcados como teste.
              </p>
              
              {testMode && (
                <Alert className="bg-yellow-50 border-yellow-200 mb-4">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertTitle className="text-yellow-800">Atenção</AlertTitle>
                  <AlertDescription className="text-yellow-700">
                    O sistema está em modo de teste. Pagamentos não serão processados.
                  </AlertDescription>
                </Alert>
              )}
            </div>
            
            <Switch
              checked={testMode}
              onCheckedChange={handleToggleTestMode}
              disabled={saving}
            />
          </div>
        </Card>

        {/* Dados de Teste */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-semibold">Dados de Teste</h3>
          </div>
          
          {testDataCount && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{testDataCount.test_data_count?.users || 0}</p>
                <p className="text-xs text-slate-500">Usuários</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{testDataCount.test_data_count?.vehicles || 0}</p>
                <p className="text-xs text-slate-500">Veículos</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{testDataCount.test_data_count?.subscriptions || 0}</p>
                <p className="text-xs text-slate-500">Assinaturas</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold">{testDataCount.test_data_count?.appointments || 0}</p>
                <p className="text-xs text-slate-500">Agendamentos</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-primary">{testDataCount.total || 0}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          )}
          
          <Button 
            variant="outline" 
            onClick={handleDeleteTestData}
            disabled={deleting || (testDataCount?.total === 0)}
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Remover Dados de Teste
          </Button>
        </Card>

        {/* Reset Database */}
        <Card className="p-6 border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-red-600">Zona de Perigo</h3>
          </div>
          
          <Alert className="bg-red-50 border-red-200 mb-4">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-800">Atenção!</AlertTitle>
            <AlertDescription className="text-red-700">
              Esta ação é <strong>IRREVERSÍVEL</strong>. Todos os dados (clientes, prestadores, agendamentos, pagamentos) 
              serão permanentemente removidos. Apenas o usuário administrador será mantido.
            </AlertDescription>
          </Alert>
          
          <Button 
            variant="destructive" 
            onClick={handleResetDatabase}
            disabled={resetting}
          >
            {resetting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Resetar Banco de Dados
          </Button>
        </Card>

        {/* Info */}
        <Card className="p-6 bg-slate-50">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-2">Sobre as Configurações</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Modo Teste:</strong> Útil para desenvolvimento e demonstrações</li>
                <li>• <strong>Dados de Teste:</strong> Podem ser removidos em massa a qualquer momento</li>
                <li>• <strong>Reset do Banco:</strong> Use apenas quando necessário recomeçar do zero</li>
                <li>• Apenas administradores podem alterar estas configurações</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
